import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ROUTE_META = [
  {
    match: /^\/$/,
    title: 'TrustSurgery | Chirurghi verificati in Italia',
    description:
      'Trova chirurghi verificati e cliniche affidabili in Italia. Cerca per specializzazione, città o clinica con TrustSurgery.',
  },
  {
    match: /^\/surgeons$/,
    title: 'Trova chirurghi verificati | TrustSurgery',
    description:
      'Esplora i profili dei chirurghi verificati, confronta le specializzazioni e trova cliniche di fiducia in tutta Italia.',
  },
  {
    match: /^\/surgeons\/[^/]+$/,
    title: 'Profilo chirurgo | TrustSurgery',
    description:
      'Consulta specializzazione, esperienza, clinica e recensioni del chirurgo su TrustSurgery-ITA.',
  },
  {
    match: /^\/about$/,
    title: 'Chi siamo | TrustSurgery',
    description:
      'Scopri la missione di TrustSurgery: connettere i pazienti con chirurghi verificati e cliniche affidabili in Italia.',
  },
  {
    match: /^\/faq$/,
    title: 'Domande frequenti (FAQ) | TrustSurgery',
    description:
      'Risposte alle domande su verifica dei chirurghi, profili, registrazione e utilizzo della piattaforma TrustSurgery.',
  },
  {
    match: /^\/how-it-works$/,
    title: 'Come funziona | TrustSurgery',
    description:
      'Scopri come TrustSurgery aiuta i pazienti a trovare chirurghi qualificati e i professionisti a far crescere il proprio profilo.',
  },
  {
    match: /^\/blog$/,
    title: 'Blog | TrustSurgery',
    description:
      'Articoli su chirurgia, sicurezza e salute: informazioni utili per pazienti e professionisti su TrustSurgery-ITA.',
  },
  {
    match: /^\/blog\/[^/]+$/,
    title: 'Articolo | TrustSurgery Blog',
    description:
      'Leggi l’articolo sul blog TrustSurgery dedicato a chirurgia, sicurezza e consapevolezza in ambito sanitario.',
  },
  {
    match: /^\/terms$/,
    title: 'Termini e Condizioni | TrustSurgery',
    description: 'Termini e condizioni di utilizzo della piattaforma TrustSurgery-ITA.',
  },
  {
    match: /^\/privacy$/,
    title: 'Informativa Privacy | TrustSurgery',
    description: 'Informativa sul trattamento dei dati personali di TrustSurgery-ITA (GDPR).',
  },
  {
    match: /^\/cookie-policy$/,
    title: 'Cookie Policy | TrustSurgery',
    description: 'Informativa sui cookie utilizzati sul sito TrustSurgery-ITA.',
  },
  {
    match: /^\/auth\/login$/,
    title: 'Accedi | TrustSurgery',
    description: 'Accedi al tuo account TrustSurgery per gestire profilo, ricerche e dashboard.',
    noIndex: true,
  },
  {
    match: /^\/auth\/register$/,
    title: 'Registrati come chirurgo | TrustSurgery',
    description:
      'Crea il tuo account chirurgo su TrustSurgery e invia i dati professionali per la verifica.',
    noIndex: true,
  },
  {
    match: /^\/auth\/verify-identity$/,
    title: 'Registrati come utente | TrustSurgery',
    description:
      'Crea un account utente TrustSurgery per cercare chirurghi, vedere i profili e gestire le preferenze.',
    noIndex: true,
  },
  {
    match: /^\/auth\/reset-password$/,
    title: 'Reimposta password | TrustSurgery',
    description: 'Reimposta in modo sicuro la password del tuo account TrustSurgery.',
    noIndex: true,
  },
  {
    match: /^\/auth\/set-password$/,
    title: 'Imposta password | TrustSurgery',
    description: 'Imposta una nuova password sicura per il tuo account TrustSurgery.',
    noIndex: true,
  },
  {
    match: /^\/auth\/pricing$/,
    title: 'Piani e tariffe | TrustSurgery',
    description:
      'Scopri i piani di abbonamento TrustSurgery per chirurghi e scegli il pacchetto più adatto alle tue esigenze.',
  },
  {
    match: /^\/surgeon\/profile$/,
    title: 'Area chirurgo | TrustSurgery',
    description: 'Gestisci profilo, credenziali e impostazioni dell’account chirurgo su TrustSurgery.',
    noIndex: true,
  },
  {
    match: /^\/dash(\/.*)?$/,
    title: 'Admin | TrustSurgery',
    description: 'Pannello di amministrazione TrustSurgery.',
    noIndex: true,
  },
  {
    match: /^\/user$/,
    title: 'Area utente | TrustSurgery',
    description: 'Gestisci il tuo account utente TrustSurgery.',
    noIndex: true,
  },
];

const DEFAULT_META = {
  title: 'TrustSurgery | Chirurghi verificati in Italia',
  description:
    'TrustSurgery aiuta i pazienti a trovare chirurghi verificati e cliniche affidabili in Italia.',
};

const upsertMeta = (attr, key, value) => {
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const MetaManager = () => {
  const location = useLocation();

  useEffect(() => {
    const routeMeta = ROUTE_META.find((entry) => entry.match.test(location.pathname)) || DEFAULT_META;

    document.title = routeMeta.title;
    upsertMeta('name', 'description', routeMeta.description);

    if (routeMeta.noIndex) {
      upsertMeta('name', 'robots', 'noindex, nofollow');
    } else {
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag) robotsTag.remove();
    }

    // Open Graph (basic)
    upsertMeta('property', 'og:title', routeMeta.title);
    upsertMeta('property', 'og:description', routeMeta.description);
    upsertMeta('property', 'og:url', window.location.href);
    upsertMeta('property', 'og:locale', 'it_IT');
    upsertMeta('property', 'og:site_name', 'TrustSurgery');

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: routeMeta.title,
        page_location: window.location.href,
      });
    }
  }, [location.pathname, location.search]);

  return null;
};

export default MetaManager;
